import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppearancePrefs } from '@/hooks/useAppearance';
import { PRESETS } from '@/lib/appearance';
import { cn } from '@/lib/cn';
import { InterruttoreV3, NotaTemiV3, useV3, useV3Disponibile } from '@/lib/v3';

/**
 * Appearance section for the Settings screen: pick a theme preset or set a
 * custom background + accent. Live-applies through `useAppearance` (mounted once, in AppShell: CSS vars +
 * Telegram chrome) and persists to localStorage; this component only reads the prefs (`useAppearancePrefs`). Phase 1 — backend-synced /
 * unlockable / AI-generated themes come in Phase 3.
 */
export function AppearanceSettings() {
  const { t } = useTranslation('general');
  const { prefs, presetId, setPreset, setCustomColors, reset } = useAppearancePrefs();

  const customAccent = prefs.customAccent ?? '#00e7df';
  // «Prova la v3»: lo vede solo chi ha il flag di build E il ruolo `beta`. Per tutti gli altri il ramo non esiste.
  const v3Disponibile = useV3Disponibile();
  // Con la v3 ACCESA vale la sua palette e il tema salvato non ha effetto: i controlli dei temi si spengono (`inert`) e una riga lo dice
  // (tma-ui-orcr su #753). Spenta, ogni classe e ogni attributo sono quelli di prima.
  const v3Acceso = useV3();

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
        {t('theme', { defaultValue: 'Theme' })}
      </h2>

      {v3Acceso && NotaTemiV3 && (
        <Suspense fallback={null}>
          <NotaTemiV3 />
        </Suspense>
      )}

      {/* Preset swatches */}
      <div className={cn('grid grid-cols-2 gap-2', v3Acceso && 'opacity-50')} inert={v3Acceso || undefined}>
        {PRESETS.map((preset) => {
          const active = presetId === preset.id;
          const pal = preset.palette;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => setPreset(preset.id)}
              className={cn(
                'flex items-center gap-3 rounded-xl border p-3 text-left transition-opacity active:opacity-80',
                active && !v3Acceso ? 'border-brand' : 'border-border',
              )}
            >
              <span
                aria-hidden
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                style={{
                  backgroundColor: pal['--color-bg'],
                  boxShadow: `inset 0 0 0 1px ${pal['--color-border']}`,
                }}
              >
                <span
                  className="h-4 w-4 rounded-full"
                  style={{ backgroundColor: pal['--color-brand'] }}
                />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-fg">{preset.name}</span>
                {active && !v3Acceso && (
                  <span className="text-xs font-medium text-brand">
                    {t('active', { defaultValue: 'Active' })}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {/* Custom colours */}
      <div className={cn('card-edge flex flex-col rounded-xl bg-bg-secondary', v3Acceso && 'opacity-50')} inert={v3Acceso || undefined}>
        <span className="px-3 pt-3 text-xs font-semibold uppercase tracking-wide text-fg-muted">
          {t('customColors', { defaultValue: 'Custom colours' })}
        </span>
        {/* Il selettore dello SFONDO non c'è più: le superfici restano verde
            acqua in ogni tema, perché la texture di fondo dell'app è
            un'immagine teal fissa. Offrire un fondo libero avrebbe promesso
            qualcosa che il risultato non poteva mantenere — superfici viola
            sopra scaglie verdi. Si sceglie solo l'accento. */}
        <label className="flex items-center justify-between px-3 py-3">
          <span className="text-sm text-fg">{t('accent', { defaultValue: 'Accent' })}</span>
          <input
            type="color"
            aria-label={t('accent', { defaultValue: 'Accent' })}
            value={customAccent}
            onChange={(e) => setCustomColors({ accent: e.target.value })}
            className="h-8 w-12 cursor-pointer rounded border border-border bg-transparent"
          />
        </label>
      </div>

      <button
        type="button"
        onClick={reset}
        inert={v3Acceso || undefined}
        className={cn('self-start text-xs font-medium text-fg-muted transition-opacity active:opacity-80', v3Acceso && 'opacity-50')}
      >
        {t('resetTheme', { defaultValue: 'Reset to default' })}
      </button>

      {/* In FONDO alla sezione, e non in cima: niente di cio' che sta sopra si sposta, quindi per chi non lo vede (e per chi
          lo vede a interruttore spento) la schermata e' pixel per pixel quella di prima, salvo la riga stessa. */}
      {v3Disponibile && InterruttoreV3 && (
        <Suspense fallback={null}>
          <InterruttoreV3 />
        </Suspense>
      )}
    </section>
  );
}
